import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { CARS, MAKES, YEARS, OTHER } from "@/lib/cars";
import { useT } from "@/lib/i18n";

export const EMPTY_CAR = { make: "", model: "", engine: "", year: "" };
const TXT = {
  no: { choose: "Velg…", make: "Bilmerke *", otherMake: "Annet merke", makePh: "Skriv bilmerke", model: "Modell *", otherModel: "Annen modell", modelPh: "Skriv modell", engine: "Motor", otherEngine: "Annen motor", enginePh: "Skriv motor", engineEx: "F.eks. 2.0 TDI", year: "Årsmodell" },
  en: { choose: "Select…", make: "Car make *", otherMake: "Other make", makePh: "Enter car make", model: "Model *", otherModel: "Other model", modelPh: "Enter model", engine: "Engine", otherEngine: "Other engine", enginePh: "Enter engine", engineEx: "E.g. 2.0 TDI", year: "Year" },
};

const Sel = ({ label, value, options, onChange, disabled, testid, required, otherLabel }) => {
  const t = useT(TXT);
  return (
  <label htmlFor={testid} className={`block ${disabled ? "opacity-40" : ""}`}>
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
    <span className="relative mt-1 block">
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} required={required} data-testid={testid}
        className="w-full appearance-none border border-white/15 bg-[#08090b] py-3 pl-3 pr-9 text-base text-white outline-none transition-colors focus:border-[var(--red)] disabled:cursor-not-allowed">
        <option value="">{t.choose}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        {otherLabel && <option value={OTHER}>{otherLabel}</option>}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" />
    </span>
  </label>
  );
};

const Txt = ({ value, onChange, placeholder, testid }) => (
  <input id={testid} aria-label={placeholder} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required data-testid={testid} maxLength={60}
    className="mt-2 w-full border border-white/15 bg-transparent px-3 py-2.5 text-sm text-white outline-none placeholder:text-zinc-500 focus:border-[var(--red)]" />
);

export const CarPicker = ({ value, onChange, showEngine = true, prefix = "car", cols = "sm:grid-cols-2" }) => {
  const t = useT(TXT);
  const [custom, setCustom] = useState(() => {
    const make = !!value.make && !CARS[value.make];
    const model = !make && !!value.model && !CARS[value.make]?.[value.model];
    const engine = !make && !model && !!value.engine && !CARS[value.make]?.[value.model]?.includes(value.engine);
    return { make, model, engine };
  });
  const known = !custom.make && CARS[value.make];
  const models = known ? Object.keys(CARS[value.make]) : [];
  const engines = known && !custom.model ? CARS[value.make][value.model] ?? [] : [];

  const pick = (key, v, reset) => {
    const isOther = v === OTHER;
    setCustom((c) => ({ ...c, [key]: isOther, ...Object.fromEntries(reset.map((r) => [r, isOther])) }));
    onChange({ ...value, [key]: isOther ? "" : v, ...Object.fromEntries(reset.map((r) => [r, ""])) });
  };
  const set = (key) => (v) => onChange({ ...value, [key]: v });

  return (
    <div className={`grid gap-4 ${cols}`} data-testid={`${prefix}-car-picker`}>
      <div>
        <Sel label={t.make} value={custom.make ? OTHER : value.make} options={MAKES} otherLabel={t.otherMake} required
          onChange={(v) => pick("make", v, ["model", "engine"])} testid={`${prefix}-make-select`} />
        {custom.make && <Txt value={value.make} onChange={set("make")} placeholder={t.makePh} testid={`${prefix}-make-input`} />}
      </div>
      <div>
        {custom.make ? (
          <label htmlFor={`${prefix}-model-input`} className="block">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.model}</span>
            <Txt value={value.model} onChange={set("model")} placeholder={t.modelPh} testid={`${prefix}-model-input`} />
          </label>
        ) : (
          <>
            <Sel label={t.model} value={custom.model ? OTHER : value.model} options={models} otherLabel={t.otherModel} required disabled={!value.make}
              onChange={(v) => pick("model", v, ["engine"])} testid={`${prefix}-model-select`} />
            {custom.model && <Txt value={value.model} onChange={set("model")} placeholder={t.modelPh} testid={`${prefix}-model-input`} />}
          </>
        )}
      </div>
      {showEngine && (
        <div>
          {custom.make || custom.model ? (
            <label htmlFor={`${prefix}-engine-input`} className="block">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{t.engine}</span>
              <input id={`${prefix}-engine-input`} value={value.engine} onChange={(e) => set("engine")(e.target.value)} placeholder={t.engineEx} maxLength={60} data-testid={`${prefix}-engine-input`}
                className="mt-1 w-full border border-white/15 bg-transparent px-3 py-3 text-base text-white outline-none placeholder:text-zinc-500 focus:border-[var(--red)]" />
            </label>
          ) : (
            <>
              <Sel label={t.engine} value={custom.engine ? OTHER : value.engine} options={engines} otherLabel={t.otherEngine} disabled={!value.model}
                onChange={(v) => pick("engine", v, [])} testid={`${prefix}-engine-select`} />
              {custom.engine && <Txt value={value.engine} onChange={set("engine")} placeholder={t.enginePh} testid={`${prefix}-engine-text`} />}
            </>
          )}
        </div>
      )}
      <Sel label={t.year} value={value.year} options={YEARS} onChange={set("year")} testid={`${prefix}-year-select`} />
    </div>
  );
};
